import React, { useState, useEffect } from "react";
import { Icons } from "../components/Icons";
import { ListSkeleton } from "../components/SkeletonLoader";
import { useWebSocketEvent } from "../hooks/useWebSocket";
import {
  getAllUsers,
  getAllOrganizations,
  updateUserStatus,
} from "../services/api";

// --- Access Requests Page ---
const AccessRequests = () => {
  const [activeTab, setActiveTab] = useState("All");
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [batchActionsOpen, setBatchActionsOpen] = useState(false);

  const [viewingDetails, setViewingDetails] = useState(null);
  const [processingAction, setProcessingAction] = useState(null);

  // Real data from the backend instead of mock data
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  // Maps backend status values to frontend display labels
  const mapStatus = (status) => {
    if (status === "pending") return "Pending";
    if (status === "active") return "Accepted";
    if (status === "inactive") return "Rejected";
    return "Pending";
  };

  // Creates initials avatar from a full name (e.g. "Juan Dela Cruz" -> "JD")
  const getInitials = (name) => {
    const parts = name.trim().split(" ");
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };

  const adminType = (() => {
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      return user?.admin_type || null;
    } catch {
      return null;
    }
  })();
  const isOffCampusAdmin = adminType === "offcampus";

  // Fetches all users and organizations from the backend on page load
  const fetchRequests = async () => {
    try {
      setLoading(true);
      const usersRes = await getAllUsers();
      const orgsRes = await getAllOrganizations();
      const orgs = orgsRes.data;

      // Filter end users by portal / application type matching this admin
      const endUsers = usersRes.data.filter((u) => {
        if (u.role !== "end_user") return false;
        if (isOffCampusAdmin) {
          return u.application_type === "offcampus";
        }
        return u.application_type !== "offcampus";
      });

      // Maps backend user data to the frontend card format
      const mapped = endUsers.map((user) => {
        const org = orgs.find(
          (o) => o.organization_id === user.organization_id,
        );
        return {
          id: user.user_id,
          avatar: getInitials(user.full_name),
          name: user.full_name,
          position: isOffCampusAdmin || user.application_type === "offcampus" ? "" : "President",
          email: user.slsu_email,
          orgName: org ? org.name : "Unknown Organization",
          acronym: org
            ? org.acronym ||
            org.name
              .split(" ")
              .map((w) => w[0])
              .join("")
              .toUpperCase()
            : "",
          college: org ? org.college || (org.description !== "Off-Campus Activity" ? org.description : "") || "N/A" : "N/A",
          orgCategory: org ? org.organization_category || "" : "",
          presidentContact: org ? org.president_contact || "N/A" : "N/A",
          adviserName: org ? org.adviser_name || "N/A" : "N/A",
          adviserContact: org ? org.adviser_contact || "N/A" : "N/A",
          totalMembers: org && org.total_members !== null && org.total_members !== undefined ? org.total_members : "N/A",
          dateStr: "N/A",
          fullDate: "N/A",
          status: mapStatus(user.status),
          organization_id: user.organization_id,
          application_type: user.application_type || "accreditation",
          // Derive request type from the linked org's org_type.
          requestType: org && org.org_type === "existing" ? "Existing" : "New",
        };
      });
      // Sort: Pending requests always at the top, then FIFO (oldest first) within groups
      mapped.sort((a, b) => {
        if (a.status === "Pending" && b.status !== "Pending") return -1;
        if (a.status !== "Pending" && b.status === "Pending") return 1;
        return a.id - b.id;
      });
      setRequests(mapped);
    } catch (error) {
      console.error("Failed to fetch access requests:", error);
    } finally {
      setLoading(false);
    }
  };

  // Runs fetchRequests once when the page loads
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchRequests();
  }, []);

  // Real-time synchronization
  useWebSocketEvent("ACCESS_REQUEST_UPDATED", () => {
    fetchRequests();
  });

  // Counts requests per status/type for the tab labels
  const tabs = isOffCampusAdmin
    ? [
        { label: "All", count: requests.length },
        {
          label: "Pending",
          count: requests.filter((r) => r.status === "Pending").length,
        },
        {
          label: "Accepted",
          count: requests.filter((r) => r.status === "Accepted").length,
        },
        {
          label: "Rejected",
          count: requests.filter((r) => r.status === "Rejected").length,
        },
      ]
    : [
        { label: "All", count: requests.length },
        {
          label: "Pending",
          count: requests.filter((r) => r.status === "Pending").length,
        },
        {
          label: "Accepted",
          count: requests.filter((r) => r.status === "Accepted").length,
        },
        {
          label: "Rejected",
          count: requests.filter((r) => r.status === "Rejected").length,
        },
        {
          label: "Existing",
          count: requests.filter((r) => r.requestType === "Existing").length,
        },
        {
          label: "New",
          count: requests.filter((r) => r.requestType === "New").length,
        },
      ];

  const filteredRequests = requests.filter((req) => {
    if (activeTab === "All") return true;
    if (activeTab === "Existing") return req.requestType === "Existing";
    if (activeTab === "New") return req.requestType === "New";
    return req.status === activeTab;
  });

  const toggleSelectRow = (id, e) => {
    e.stopPropagation();
    if (selectedRows.includes(id)) {
      setSelectedRows(selectedRows.filter((rId) => rId !== id));
    } else {
      setSelectedRows([...selectedRows, id]);
    }
  };

  const openProcessingModal = (req, action) => {
    setViewingDetails(null);
    setProcessingAction({ request: req, action });
  };

  // Calls the backend to update BOTH the user status AND the organization status
  const confirmProcess = async () => {
    if (!processingAction) return;
    try {
      const userStatus =
        processingAction.action === "Accept" ? "active" : "inactive";

      await updateUserStatus(processingAction.request.id, userStatus);

      await fetchRequests();
      setProcessingAction(null);
    } catch (error) {
      console.error("Failed to process request:", error);
      alert("Failed to process the request. Please try again.");
    }
  };

  // Handles batch accept/reject of multiple selected requests — updates user AND org
  const handleBatchAction = async (action) => {
    setBatchActionsOpen(false);
    try {
      const userStatus = action === "Accept" ? "active" : "inactive";

      for (const id of selectedRows) {
        await updateUserStatus(id, userStatus);
      }
      await fetchRequests();
      setIsSelectMode(false);
      setSelectedRows([]);
    } catch (error) {
      console.error("Failed batch action:", error);
      alert("Some requests could not be processed.");
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col h-full bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden p-8">
        <div className="space-y-2 mb-6">
          <div className="skeleton h-7 w-48" />
          <div className="skeleton h-4 w-72" />
        </div>
        <ListSkeleton rows={6} />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden relative">
      <div className="p-8 pb-4 flex-shrink-0">
        <h2 className="text-2xl font-bold text-gray-900 tracking-tight">
          Access Requests
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          Manage student organization representative request access
        </p>
      </div>

      <div className="px-8 flex-shrink-0">
        <div className="flex justify-between items-center border-b border-gray-200 mb-6 pb-2">
          <div className="flex space-x-8">
            {tabs.map((tab) => (
              <button
                key={tab.label}
                className={`pb-3 px-1 relative font-medium text-sm transition-colors ${activeTab === tab.label ? "text-[#086C53]" : "text-gray-500 hover:text-gray-700"}`}
                onClick={() => setActiveTab(tab.label)}
              >
                {tab.label} ({tab.count})
                {activeTab === tab.label && (
                  <div className="absolute bottom-[-9px] left-0 right-0 h-[3px] bg-[#086C53] rounded-t-full"></div>
                )}
              </button>
            ))}
          </div>
          <div>
            {!isSelectMode ? (
              <button
                onClick={() => setIsSelectMode(true)}
                className="flex items-center space-x-2 border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors shadow-sm"
              >
                <Icons.Select />
                <span>Select</span>
              </button>
            ) : (
              <div className="flex items-center space-x-3">
                <div className="relative">
                  <button
                    onClick={() => setBatchActionsOpen(!batchActionsOpen)}
                    className="flex items-center space-x-2 bg-[#086C53] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#065440] transition-colors shadow-sm"
                  >
                    <span>Batch Actions</span>
                    <Icons.ChevronDown />
                  </button>
                  {batchActionsOpen && (
                    <div className="absolute top-full right-0 mt-1 w-40 bg-[#086C53] text-white rounded-lg shadow-lg z-50 py-1 overflow-hidden">
                      <div
                        className="px-4 py-2 cursor-pointer text-sm hover:bg-white/10 flex items-center space-x-2"
                        onClick={() => handleBatchAction("Accept")}
                      >
                        <Icons.Check /> <span>Accept</span>
                      </div>
                      <div
                        className="px-4 py-2 cursor-pointer text-sm hover:bg-white/10 flex items-center space-x-2"
                        onClick={() => handleBatchAction("Reject")}
                      >
                        <Icons.Close /> <span>Reject</span>
                      </div>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => {
                    setIsSelectMode(false);
                    setSelectedRows([]);
                  }}
                  className="flex items-center space-x-2 border border-gray-300 bg-gray-100 text-gray-600 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-200 transition-colors shadow-sm"
                >
                  <Icons.Select />
                  <span>Cancel</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-8 pb-8 custom-scrollbar">
        <div className="space-y-4 max-w-4xl">
          {filteredRequests.length === 0 ? (
            <p className="text-gray-400 text-sm text-center py-10">
              No access requests found.
            </p>
          ) : (
            filteredRequests.map((req) => (
              <div
                key={req.id}
                className={`bg-white border ${selectedRows.includes(req.id) ? "border-[#086C53]" : "border-gray-200"} rounded-xl p-5 flex items-center shadow-sm hover:shadow-md transition-shadow cursor-pointer relative group`}
                onClick={(e) => {
                  if (isSelectMode) {
                    toggleSelectRow(req.id, e);
                  } else {
                    setViewingDetails(req);
                  }
                }}
              >
                {isSelectMode && (
                  <div className="mr-5">
                    <div
                      className={`w-5 h-5 rounded flex items-center justify-center cursor-pointer border transition-colors ${selectedRows.includes(req.id) ? "bg-[#086C53] border-[#086C53] text-white" : "border-gray-300 group-hover:border-[#086C53]"}`}
                    >
                      {selectedRows.includes(req.id) && (
                        <Icons.Check className="w-3 h-3" />
                      )}
                    </div>
                  </div>
                )}

                <div className="flex-1 flex space-x-5 items-center">
                  <div className="w-[52px] h-[52px] rounded-full bg-[#086C53] text-white flex items-center justify-center text-lg font-bold flex-shrink-0">
                    {req.avatar}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-0.5">
                      <h4 className="text-gray-900 font-semibold text-base">
                        {req.name}
                      </h4>
                      {!isOffCampusAdmin && req.application_type !== "offcampus" && req.requestType === "Existing" && (
                        <span className="inline-flex items-center space-x-1 text-orange-600 text-[10px] font-bold px-2 py-0.5 rounded-full border border-orange-400">
                          <span>Existing</span>
                        </span>
                      )}
                      {!isOffCampusAdmin && req.application_type !== "offcampus" && req.requestType === "New" && (
                        <span className="inline-flex items-center space-x-1 text-green-600 text-[10px] font-bold px-2 py-0.5 rounded-full border border-green-400">
                          <span>New</span>
                        </span>
                      )}
                    </div>
                    {!(isOffCampusAdmin || req.application_type === "offcampus") && req.position && (
                      <p className="text-gray-500 text-xs mb-2">{req.position}</p>
                    )}

                    <div className={`flex items-center space-x-5 text-gray-500 text-[11px] font-medium ${isOffCampusAdmin || req.application_type === "offcampus" ? "mt-1.5" : ""}`}>
                      <div className="flex items-center space-x-1.5">
                        <Icons.Mail />
                        <span>{req.email}</span>
                      </div>
                      <div className="flex items-center space-x-1.5">
                        <Icons.Users />
                        <span>{req.orgName}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="ml-4 flex-shrink-0">
                  {req.status === "Pending" && (
                    <button
                      className="bg-[#086C53] text-white px-5 py-2.5 rounded-lg text-sm font-semibold hover:bg-[#065440] transition-colors shadow-sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        openProcessingModal(req, null);
                      }}
                    >
                      Process Request
                    </button>
                  )}
                  {req.status === "Accepted" && (
                    <div className="bg-[#D1FAE5] text-[#065F46] px-5 py-2.5 rounded-lg text-sm font-bold flex items-center justify-center min-w-[140px]">
                      Accepted
                    </div>
                  )}
                  {req.status === "Rejected" && (
                    <div className="bg-[#FEE2E2] text-[#991B1B] px-5 py-2.5 rounded-lg text-sm font-bold flex items-center justify-center min-w-[140px]">
                      Rejected
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Modal 1: Details */}
      {viewingDetails && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[100] p-4 backdrop-blur-sm">
          <div
            className="bg-white rounded-[24px] w-full max-w-[500px] shadow-2xl overflow-hidden flex flex-col max-h-[90vh] relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setViewingDetails(null)}
              className="absolute top-6 right-6 text-gray-400 hover:text-gray-600 transition-colors bg-gray-50 hover:bg-gray-100 rounded-full p-2"
            >
              <Icons.Close />
            </button>
            <div className="p-8 overflow-y-auto">
              <h2 className="text-xl font-bold text-gray-900 mb-1">
                Access Request Details
              </h2>
              <p className="text-sm text-gray-500 mb-8">
                User ID: {viewingDetails.id}
              </p>

              <div className="space-y-8">
                <div>
                  <h3 className="font-bold text-gray-800 text-sm mb-4">
                    Personal Information
                  </h3>
                  <div className="space-y-4 text-sm">
                    <div>
                      <p className="text-gray-400 text-xs mb-0.5">Full Name</p>
                      <p className="font-semibold text-gray-900">
                        {viewingDetails.name}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-400 text-xs mb-0.5">Email</p>
                      <p className="font-semibold text-gray-900">
                        {viewingDetails.email}
                      </p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="font-bold text-gray-800 text-sm mb-4">
                    {isOffCampusAdmin || viewingDetails.application_type === "offcampus"
                      ? "Activity Information"
                      : "Organization Information"}
                  </h3>
                  <div className="grid grid-cols-2 gap-y-4 gap-x-6 text-sm">
                    <div className={isOffCampusAdmin || viewingDetails.application_type === "offcampus" ? "col-span-2" : ""}>
                      <p className="text-gray-400 text-xs mb-0.5">
                        {isOffCampusAdmin || viewingDetails.application_type === "offcampus"
                          ? "Activity Name"
                          : "Organization Name"}
                      </p>
                      <p className="font-semibold text-gray-900">
                        {viewingDetails.orgName}
                      </p>
                    </div>

                    {!(isOffCampusAdmin || viewingDetails.application_type === "offcampus") && (
                      <div>
                        <p className="text-gray-400 text-xs mb-0.5">Acronym</p>
                        <p className="font-semibold text-gray-900">
                          {viewingDetails.acronym || "—"}
                        </p>
                      </div>
                    )}

                    {!(isOffCampusAdmin || viewingDetails.application_type === "offcampus") && viewingDetails.orgCategory && (
                      <div>
                        <p className="text-gray-400 text-xs mb-0.5">Type of Organization</p>
                        <p className="font-semibold text-gray-900">
                          {viewingDetails.orgCategory}
                        </p>
                      </div>
                    )}

                    {!(isOffCampusAdmin || viewingDetails.application_type === "offcampus") && (
                      <div>
                        <p className="text-gray-400 text-xs mb-0.5">Position</p>
                        <p className="font-semibold text-gray-900">
                          {viewingDetails.position}
                        </p>
                      </div>
                    )}

                    <div className={isOffCampusAdmin || viewingDetails.application_type === "offcampus" ? "col-span-2" : ""}>
                      <p className="text-gray-400 text-xs mb-0.5">
                        {isOffCampusAdmin || viewingDetails.application_type === "offcampus"
                          ? "College / Campus"
                          : "College"}
                      </p>
                      <p className="font-semibold text-gray-900">
                        {viewingDetails.college}
                      </p>
                    </div>

                    {/* Accreditation specific fields */}
                    {!(isOffCampusAdmin || viewingDetails.application_type === "offcampus") && (
                      <>
                        {viewingDetails.presidentContact && viewingDetails.presidentContact !== "N/A" && (
                          <div>
                            <p className="text-gray-400 text-xs mb-0.5">President Contact</p>
                            <p className="font-semibold text-gray-900">
                              {viewingDetails.presidentContact}
                            </p>
                          </div>
                        )}
                        {viewingDetails.adviserName && viewingDetails.adviserName !== "N/A" && (
                          <div>
                            <p className="text-gray-400 text-xs mb-0.5">Adviser Name</p>
                            <p className="font-semibold text-gray-900">
                              {viewingDetails.adviserName}
                            </p>
                          </div>
                        )}
                        {viewingDetails.adviserContact && viewingDetails.adviserContact !== "N/A" && (
                          <div>
                            <p className="text-gray-400 text-xs mb-0.5">Adviser Contact</p>
                            <p className="font-semibold text-gray-900">
                              {viewingDetails.adviserContact}
                            </p>
                          </div>
                        )}
                        {viewingDetails.totalMembers && viewingDetails.totalMembers !== "N/A" && (
                          <div>
                            <p className="text-gray-400 text-xs mb-0.5">Total Members</p>
                            <p className="font-semibold text-gray-900">
                              {viewingDetails.totalMembers}
                            </p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 pt-0 flex justify-end space-x-3 bg-white">
              {viewingDetails.status === "Pending" && (
                <>
                  <button
                    onClick={() =>
                      openProcessingModal(viewingDetails, "Reject")
                    }
                    className="px-6 py-2.5 border border-[#086C53] rounded-lg text-sm font-semibold text-[#086C53] hover:bg-green-50 transition-colors"
                  >
                    Reject
                  </button>
                  <button
                    onClick={() =>
                      openProcessingModal(viewingDetails, "Accept")
                    }
                    className="px-6 py-2.5 bg-[#086C53] rounded-lg text-sm font-semibold text-white hover:bg-[#065440] transition-colors shadow-sm"
                  >
                    Accept
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Modal 2: Process */}
      {processingAction && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[110] p-4 backdrop-blur-sm">
          <div
            className="bg-white rounded-[24px] w-full max-w-[480px] shadow-2xl overflow-hidden flex flex-col relative"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-8">
              <h2 className="text-[22px] font-bold text-gray-900 mb-1">
                Process Access Request
              </h2>
              <p className="text-[13px] text-gray-500 mb-8">
                {processingAction.request.name} -{" "}
                {processingAction.request.orgName}
              </p>

              <div className="flex space-x-3 mb-6 bg-gray-50 p-1.5 rounded-xl border border-gray-100">
                <button
                  onClick={() =>
                    setProcessingAction({
                      ...processingAction,
                      action: "Accept",
                    })
                  }
                  className={`flex-1 py-2.5 rounded-[10px] text-sm font-bold transition-all duration-200 ${processingAction.action === "Accept" ? "bg-[#10B981] border-transparent text-white shadow-md" : "bg-white border border-gray-300 text-[#4D4D4D] hover:bg-gray-50"}`}
                >
                  Accept
                </button>
                <button
                  onClick={() =>
                    setProcessingAction({
                      ...processingAction,
                      action: "Reject",
                    })
                  }
                  className={`flex-1 py-2.5 rounded-[10px] text-sm font-bold transition-all duration-200 ${processingAction.action === "Reject" ? "bg-[#EF4444] border-transparent text-white shadow-md" : "bg-white border border-gray-300 text-[#4D4D4D] hover:bg-gray-50"}`}
                >
                  Reject
                </button>
              </div>
            </div>

            <div className="p-8 pt-0 flex justify-end space-x-3">
              <button
                onClick={() => setProcessingAction(null)}
                className="px-8 py-2.5 border border-gray-300 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              {processingAction.action && (
                <button
                  onClick={confirmProcess}
                  className={`px-8 py-2.5 rounded-lg text-sm font-semibold text-white transition-colors shadow-sm ${
                    processingAction.action === "Reject"
                      ? "bg-[#EF4444] hover:bg-[#DC2626]"
                      : "bg-[#086C53] hover:bg-[#065440]"
                  }`}
                >
                  Confirm
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccessRequests;
